/**
 * Klein grijs "camera?" / "regie?" in de crewkolom als die rol op een draaidag nog open is.
 * Hover (of klik, voor touch) toont wie er die dag(en) kan: vaste mensen eerst, dan freelance,
 * en ingeklapt wie niet kan en waarom (Joris, 13 okt 2026).
 *
 * De gegevens komen per week in één batch (zie DraaidagenOverzicht → laadBeschikbaarheid);
 * de eerste hover op een week vraagt hem op, daarna komt alles uit het geheugen.
 */
import React, { useState } from 'react';
import { Box, ClickAwayListener, Tooltip, Typography } from '@mui/material';
import { ROL_LABEL, beperkt, tooltipVoorRol } from '../../utils/draaidagBeschikbaar';

const GRIJS = '#94a3b8';

function Namen({ lijst, testId }) {
  const { zichtbaar, meer } = beperkt(lijst);
  let freelanceKop = false;
  return (
    <Box data-testid={testId}>
      {zichtbaar.map((p) => {
        const kop = p.freelance && !freelanceKop;
        if (kop) freelanceKop = true;
        return (
          <React.Fragment key={`${p.freelance ? 'f' : 'v'}:${p.naam}`}>
            {kop && <Typography sx={{ fontSize: 10.5, color: GRIJS, mt: 0.5 }}>freelance</Typography>}
            <Typography sx={{ fontSize: 12, lineHeight: 1.5, color: '#1f2937' }}>
              {p.naam}
              {p.functie && <Box component="span" sx={{ color: GRIJS }}> · {p.functie}</Box>}
              <Box component="span" sx={{ color: '#64748b' }}> — {p.status}</Box>
            </Typography>
          </React.Fragment>
        );
      })}
      {meer > 0 && <Typography sx={{ fontSize: 11, color: GRIJS }}>+{meer} meer</Typography>}
    </Box>
  );
}

function RolBlok({ rol, inhoud }) {
  const [nietOpen, setNietOpen] = useState(false);
  const { beschikbaar, nietBeschikbaar } = inhoud;
  return (
    <Box data-testid={`open-rol-${rol}`} sx={{ '& + &': { mt: 1.25 } }}>
      <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: '#334155', mb: 0.25 }}>
        {ROL_LABEL[rol]} nog open · beschikbaar
      </Typography>
      {beschikbaar.length
        ? <Namen lijst={beschikbaar} testId={`beschikbaar-${rol}`} />
        : <Typography sx={{ fontSize: 12, color: GRIJS }}>Niemand vrij.</Typography>}
      {nietBeschikbaar.length > 0 && (
        <Box sx={{ mt: 0.5 }}>
          <Typography component="button" type="button" data-testid={`niet-beschikbaar-knop-${rol}`}
            onClick={(e) => { e.stopPropagation(); setNietOpen((v) => !v); }}
            aria-expanded={nietOpen}
            sx={{ all: 'unset', cursor: 'pointer', fontSize: 11, color: GRIJS,
              '&:focus-visible': { outline: '2px solid #6366f1' } }}>
            {nietOpen ? '▾' : '▸'} Niet beschikbaar ({nietBeschikbaar.length})
          </Typography>
          {nietOpen && <Namen lijst={nietBeschikbaar} testId={`niet-beschikbaar-${rol}`} />}
        </Box>
      )}
    </Box>
  );
}

/**
 * @param open        ['camera', 'regie'] — de rollen die nog open zijn (zie openRollen)
 * @param onbekend    titelnamen die niet in Personen staan (rol onbekend)
 * @param datums      de dagen van de draaidag
 * @param weken       { status: 'laden'|'klaar'|'fout', data: [engine-antwoorden] }
 * @param onNodig     vraagt de week-batch op (eerste hover)
 */
export default function OpenRolSignaal({ rij, open, onbekend = [], datums, weken, onNodig }) {
  const [toon, setToon] = useState(false);
  const [vast, setVast] = useState(false);
  if (!open || !open.length) return null;
  const openen = () => { onNodig?.(); setToon(true); };
  const sluiten = () => { if (!vast) setToon(false); };

  let inhoud;
  if (!weken || weken.status === 'laden') {
    inhoud = <Typography sx={{ fontSize: 12, color: GRIJS }}>Beschikbaarheid laden…</Typography>;
  } else if (weken.status === 'fout') {
    inhoud = <Typography sx={{ fontSize: 12, color: '#b91c1c' }}>Beschikbaarheid laden mislukt.</Typography>;
  } else {
    const melding = (weken.data || []).map((w) => w?.agenda?.melding).find(Boolean);
    inhoud = (
      <Box sx={{ maxWidth: 360 }}>
        {open.map((rol) => (
          <RolBlok key={rol} rol={rol} inhoud={tooltipVoorRol(weken.data, rol, rij, datums)} />
        ))}
        {onbekend.length > 0 && (
          <Typography sx={{ fontSize: 10.5, color: GRIJS, mt: 0.75 }}>
            Rol niet te bepalen: {onbekend.join(', ')}
          </Typography>
        )}
        {melding && <Typography data-testid="beschikbaar-melding" sx={{ fontSize: 10.5, color: '#b45309', mt: 0.75 }}>{melding}</Typography>}
      </Box>
    );
  }

  return (
    <ClickAwayListener onClickAway={() => { if (vast) { setVast(false); setToon(false); } }}>
      <Tooltip open={toon} onOpen={openen} onClose={sluiten} placement="bottom-start"
        title={<Box data-testid="open-rol-tooltip" onClick={(e) => e.stopPropagation()}>{inhoud}</Box>}
        componentsProps={{
          // De tooltip staat in een portal, maar React laat klikken via de componentboom doorbubbelen
          // naar de draaidag-rij (→ detailpaneel). De Box hieronder ving alleen klikken op de inhoud;
          // een klik op de rand/lege plek van de tooltip opende het paneel (testronde 14 okt 2026).
          popper: { onClick: (e) => e.stopPropagation() },
          tooltip: { sx: { bgcolor: '#fff', color: '#1f2937', border: '1px solid #e2e8f0',
            boxShadow: '0 4px 16px rgba(15,23,42,0.08)', p: 1.25, maxWidth: 380 } },
        }}>
        <Typography component="span" role="button" tabIndex={0} data-testid={`open-rol-signaal-${rij.id}`}
          aria-label={`${open.map((r) => ROL_LABEL[r]).join(' en ')} nog open — wie is er beschikbaar`}
          onClick={(e) => { e.stopPropagation(); if (vast) { setVast(false); setToon(false); } else { setVast(true); openen(); } }}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setVast(true); openen(); } if (e.key === 'Escape') { setVast(false); setToon(false); } }}
          sx={{ fontSize: 11, color: GRIJS, cursor: 'help', whiteSpace: 'nowrap', lineHeight: '20px',
            '&:hover, &:focus-visible': { color: '#475569' }, '&:focus-visible': { outline: '2px solid #6366f1', borderRadius: 1 } }}>
          {open.map((r) => `${r}?`).join(' ')}
        </Typography>
      </Tooltip>
    </ClickAwayListener>
  );
}
